// src/pages/ReportEditor.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Heading,
  Divider,
  Tag,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Textarea,
  Select,
  Button,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { auth, db, storage } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as sRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const MB10 = 10 * 1024 * 1024;
const isPdf = (f) => !!f && f.type === 'application/pdf';
const toNum = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

export default function ReportEditor({ leadId, onClose }) {
  const [user, setUser] = useState(auth.currentUser || null);
  const [orgId, setOrgId] = useState(null);

  const [lead, setLead] = useState(null);
  const [settings, setSettings] = useState({ requireContractPdf: true });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState('draft'); // draft|submitted|unlocked|discarded

  // form
  const [contractNumber, setContractNumber] = useState('');
  const [contractPrice, setContractPrice] = useState('');
  const [adminAdj, setAdminAdj] = useState('');
  const orderPrice = useMemo(
    () => round2(toNum(contractPrice) + toNum(adminAdj)),
    [contractPrice, adminAdj]
  );
  const [itemsSold, setItemsSold] = useState('');
  const [specialArrangements, setSpecialArrangements] = useState('');

  const [adminPaid, setAdminPaid] = useState('N/A');
  const [adminPaidDate, setAdminPaidDate] = useState('');
  const [adminTbpDate, setAdminTbpDate] = useState('');
  const [adminPaymentMethod, setAdminPaymentMethod] = useState('N/A');

  const [paymentType, setPaymentType] = useState('');

  const [cashTerm, setCashTerm] = useState('');
  const [cashDepositAmount, setCashDepositAmount] = useState('');
  const [cashDepositMethod, setCashDepositMethod] = useState('N/A');
  
  useEffect(() => {
    if (paymentType === 'Cash' && !cashTerm) setCashTerm('preset1'); // auto-default
  }, [paymentType, cashTerm]);
  
  const cashDepositPct = useMemo(() => {
    const cp = toNum(contractPrice),
      dep = toNum(cashDepositAmount);
    if (!cp || cp <= 0 || dep <= 0) return 0;
    return round2((dep / cp) * 100);
  }, [contractPrice, cashDepositAmount]);

  const [cfCashAmount, setCfCashAmount] = useState('');
  const [cfCashMethod, setCfCashMethod] = useState('N/A');
  const cfCashPct = useMemo(() => {
    const cp = toNum(contractPrice),
      ca = toNum(cfCashAmount);
    if (!cp || cp <= 0 || ca <= 0) return 0;
    return round2((ca / cp) * 100);
  }, [contractPrice, cfCashAmount]);
  const [cfFinanceAmount, setCfFinanceAmount] = useState('');

  const [financeSigned, setFinanceSigned] = useState('');
  const [financeResult, setFinanceResult] = useState('');
  const [financeHouse, setFinanceHouse] = useState('');

  const [contractPdfUrl, setContractPdfUrl] = useState('');
  const [doorsPdfUrl, setDoorsPdfUrl] = useState('');
  const [contractPdfFile, setContractPdfFile] = useState(null);
  const [doorsPdfFile, setDoorsPdfFile] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (!u) {
        setOrgId(null);
        setLoading(false);
        return;
      }
      const token = await u.getIdTokenResult(true);
      setOrgId(token?.claims?.orgId || null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!orgId || !leadId) return;
        setLoading(true);

        // settings
        const appRef = doc(db, 'organizations', orgId, 'settings', 'app');
        const appSnap = await getDoc(appRef);
        if (appSnap.exists()) {
          const cfg = appSnap.data() || {};
          setSettings({
            requireContractPdf: cfg.hasOwnProperty('requireContractPdf')
              ? !!cfg.requireContractPdf
              : true,
          });
        }

        // lead
        const leadRef = doc(db, 'organizations', orgId, 'leads', leadId);
        const leadSnap = await getDoc(leadRef);
        setLead(leadSnap.exists() ? { id: leadSnap.id, ...leadSnap.data() } : { id: leadId });

        // report
        const repRef = doc(db, 'organizations', orgId, 'leads', leadId, 'salesReport', 'current');
        const repSnap = await getDoc(repRef);
        if (repSnap.exists()) {
          const d = repSnap.data() || {};
          setStatus(d.status || 'draft');

          setContractNumber(d.contractNumber ?? '');
          setContractPrice((d.contractPrice ?? '').toString());
          setAdminAdj((d.admin ?? '').toString());
          setItemsSold(d.itemsSold ?? '');
          setSpecialArrangements(d.specialArrangements ?? '');

          setAdminPaid(d.adminPaid ?? 'N/A');
          setAdminPaidDate(d.adminPaidDate ?? '');
          setAdminTbpDate(d.adminTbpDate ?? '');
          setAdminPaymentMethod(d.adminPaymentMethod ?? 'N/A');

          setPaymentType(d.contractPayment ?? '');
          setCashTerm(d.cash?.term ?? '');
          setCashDepositAmount((d.cash?.depositAmount ?? '').toString());
          setCashDepositMethod(d.cash?.depositMethod ?? 'N/A');

          setCfCashAmount((d.cashAndFinance?.cashAmount ?? '').toString());
          setCfCashMethod(d.cashAndFinance?.cashMethod ?? 'N/A');
          setCfFinanceAmount((d.cashAndFinance?.financeAmount ?? '').toString());

          setFinanceSigned(d.finance?.applicationSigned ?? '');
          setFinanceResult(d.finance?.result ?? '');
          setFinanceHouse(d.finance?.house ?? '');

          setContractPdfUrl(d.attachments?.contractPdfUrl || '');
          setDoorsPdfUrl(d.attachments?.doorsPdfUrl || '');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, leadId]);

  const toast = useToast();

  function validate() {
    if (!contractNumber.trim()) return 'Contract Number is required.';
    if (contractNumber.trim().length > 10) return 'Contract Number must be at most 10 characters.';
    if (!itemsSold.trim()) return 'Items Sold is required.';
    const cp = toNum(contractPrice),
      ad = toNum(adminAdj);
    if (isNaN(cp) || cp < 0) return 'Contract Price must be 0 or more.';
    if (isNaN(ad) || ad < 0) return 'Admin must be 0 or more.';

    if (adminPaid === 'Yes') {
      if (!adminPaidDate) return 'Please select the Admin Paid date.';
      if (!adminPaymentMethod || adminPaymentMethod === 'N/A')
        return 'Select Admin Payment Method.';
    }
    if (adminPaid === 'TBP') {
      if (!adminTbpDate) return 'Please select the Admin Target date.';
      if (!adminPaymentMethod || adminPaymentMethod === 'N/A')
        return 'Select planned Admin Payment Method.';
    }

    if (!paymentType) return 'Select Contract Payment.';
    if (paymentType === 'Cash') {
      if (!cashTerm) return 'Select the Cash term.';
      if (toNum(cashDepositAmount) > cp) return 'Deposit cannot exceed Contract Price.';
      if (toNum(cashDepositAmount) > 0 && (!cashDepositMethod || cashDepositMethod === 'N/A')) {
        return 'Select deposit payment method.';
      }
    }
    if (paymentType === 'Finance') {
      if (!financeSigned) return 'Select if finance application is signed.';
      if (financeSigned === 'Yes') {
        if (!financeResult) return 'Select finance result.';
        if (!financeHouse) return 'Select finance house.';
      }
    }
    if (paymentType === 'Cash & Finance') {
      const ca = toNum(cfCashAmount),
        fa = toNum(cfFinanceAmount);
      if (!ca || !fa) return 'Cash amount and Finance amount are required.';
      if (ca + fa > cp) return 'Cash + Finance cannot exceed Contract Price.';
      if (!cfCashMethod || cfCashMethod === 'N/A')
        return 'Select deposit payment method for the Cash part.';
      if (!financeSigned) return 'Select if finance application is signed.';
      if (financeSigned === 'Yes') {
        if (!financeResult) return 'Select finance result.';
        if (!financeHouse) return 'Select finance house.';
      }
    }

    if (settings.requireContractPdf && !contractPdfUrl && !contractPdfFile) {
      return 'Contract PDF is required.';
    }
    if (contractPdfFile && (!isPdf(contractPdfFile) || contractPdfFile.size > MB10)) {
      return 'Contract PDF must be a PDF up to 10 MB.';
    }
    if (doorsPdfFile && (!isPdf(doorsPdfFile) || doorsPdfFile.size > MB10)) {
      return 'Doors PDF must be a PDF up to 10 MB.';
    }
    return null;
  }

  async function uploadPdf(path, file) {
    const storageRef = sRef(storage, path);
    const task = uploadBytesResumable(storageRef, file, { contentType: 'application/pdf' });
    await new Promise((resolve, reject) => task.on('state_changed', null, reject, resolve));
    return getDownloadURL(task.snapshot.ref);
  }

  async function handleSave(keepStatus = true) {
    try {
      const err = validate();
      if (err) {
        toast({ title: 'Cannot save', description: err, status: 'warning' });
        return;
      }
      setSaving(true);

      let newContractUrl = contractPdfUrl;
      let newDoorsUrl = doorsPdfUrl;
      if (contractPdfFile) {
        newContractUrl = await uploadPdf(
          `organizations/${orgId}/leads/${leadId}/reports/current/contract.pdf`,
          contractPdfFile
        );
      }
      if (doorsPdfFile) {
        newDoorsUrl = await uploadPdf(
          `organizations/${orgId}/leads/${leadId}/reports/current/doors.pdf`,
          doorsPdfFile
        );
      }

      const payload = {
        status: keepStatus ? status : 'submitted',
        updatedAt: serverTimestamp(),
        updatedByUid: user?.uid || null,

        contractNumber: contractNumber.trim(),
        contractPrice: Number(toNum(contractPrice)),
        admin: Number(toNum(adminAdj)),
        orderPrice: Number(round2(toNum(contractPrice) + toNum(adminAdj))),

        itemsSold: itemsSold.trim(),
        specialArrangements: specialArrangements.trim(),

        adminPaid,
        adminPaidDate: adminPaid === 'Yes' ? adminPaidDate || null : null,
        adminTbpDate: adminPaid === 'TBP' ? adminTbpDate || null : null,
        adminPaymentMethod:
          adminPaid === 'Yes' || adminPaid === 'TBP' ? adminPaymentMethod || 'N/A' : 'N/A',

        contractPayment: paymentType || '',

        cash: {
          term: paymentType === 'Cash' ? cashTerm || '' : '',
          depositAmount:
            paymentType === 'Cash'
              ? cashDepositAmount === ''
                ? null
                : Number(cashDepositAmount)
              : null,
          depositMethod: paymentType === 'Cash' ? cashDepositMethod || 'N/A' : 'N/A',
          depositPct:
            paymentType === 'Cash'
              ? Number(
                  toNum(contractPrice) && toNum(cashDepositAmount)
                    ? round2((toNum(cashDepositAmount) / toNum(contractPrice)) * 100)
                    : 0
                )
              : null,
        },

        cashAndFinance: {
          cashAmount: paymentType === 'Cash & Finance' ? Number(toNum(cfCashAmount)) : null,
          cashMethod: paymentType === 'Cash & Finance' ? cfCashMethod || 'N/A' : 'N/A',
          cashPct:
            paymentType === 'Cash & Finance'
              ? Number(
                  toNum(contractPrice) && toNum(cfCashAmount)
                    ? round2((toNum(cfCashAmount) / toNum(contractPrice)) * 100)
                    : 0
                )
              : null,
          financeAmount: paymentType === 'Cash & Finance' ? Number(toNum(cfFinanceAmount)) : null,
        },

        finance: {
          hasFinance: paymentType === 'Finance' || paymentType === 'Cash & Finance',
          applicationSigned:
            paymentType === 'Finance' || paymentType === 'Cash & Finance'
              ? financeSigned || ''
              : '',
          result: financeSigned === 'Yes' ? financeResult || '' : '',
          house: financeSigned === 'Yes' ? financeHouse || '' : '',
        },

        attachments: {
          contractPdfUrl: newContractUrl || null,
          doorsPdfUrl: newDoorsUrl || null,
        },
      };

      const repRef = doc(db, 'organizations', orgId, 'leads', leadId, 'salesReport', 'current');
      await setDoc(repRef, payload, { merge: true });

      if (!keepStatus) setStatus('submitted');
      toast({ title: 'Saved', status: 'success' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message || String(e), status: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function unlock() {
    try {
      await setDoc(
        doc(db, 'organizations', orgId, 'leads', leadId, 'salesReport', 'current'),
        { status: 'unlocked', updatedAt: serverTimestamp(), unlockedByUid: user?.uid || null },
        { merge: true }
      );
    } catch (e) {
      toast({ title: 'Action failed', description: e.message || String(e), status: 'error' });
      return;
    }
    setStatus('unlocked');
    toast({ title: 'Unlocked for rep', status: 'info' });
  }

  // SAVE & SUBMIT: relock, then mark the lead as SOLD (idempotent)
  async function relockSubmit() {
    try {
      await handleSave(false); // save & set status → 'submitted'

      // Flip lead to SOLD (safe if already SOLD)
      await setDoc(
        doc(db, 'organizations', orgId, 'leads', leadId),
        {
          status: 'SOLD',
          postSaleStage: 'awaiting_survey',
          soldAt: serverTimestamp(),
          soldByUid: user?.uid || null,
        },
        { merge: true }
      );

      setStatus('submitted');
      toast({ title: 'Sales report submitted — lead marked SOLD', status: 'success' });
    } catch (e) {
      toast({ title: 'Submit failed', description: e.message || String(e), status: 'error' });
    }
  }

  if (!user || !orgId)
    return (
      <HStack>
        <Spinner />
        <Text>Loading…</Text>
      </HStack>
    );
  if (loading)
    return (
      <HStack>
        <Spinner />
        <Text>Loading…</Text>
      </HStack>
    );

  const customerName =
    lead?.customerFullName ||
    `${lead?.customerFirstName || ''} ${lead?.customerLastName || ''}`.trim();

  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Heading size="sm">Report for: {customerName || '—'}</Heading>
        <Tag
          colorScheme={status === 'submitted' ? 'purple' : status === 'unlocked' ? 'blue' : 'gray'}
        >
          {status}
        </Tag>
      </HStack>
      <Divider mb={4} />

      {status === 'unlocked' && (
        <Alert status="info" mb={4}>
          <AlertIcon />
          This report is unlocked for the rep. You can edit here or relock to submit.
        </Alert>
      )}

      <VStack align="stretch" spacing={3}>
        <SimpleGrid columns={[1, 2]} spacing={3}>
          <FormControl isRequired>
            <FormLabel>Contract Number</FormLabel>
            <Input
              value={contractNumber}
              onChange={(e) => setContractNumber(e.target.value.slice(0, 10))}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Order Price (auto)</FormLabel>
            <Input value={orderPrice.toFixed(2)} readOnly />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Contract Price (£)</FormLabel>
            <NumberInput min={0} value={contractPrice} onChange={(v) => setContractPrice(v)}>
              <NumberInputField />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Admin (£)</FormLabel>
            <NumberInput min={0} value={adminAdj} onChange={(v) => setAdminAdj(v)}>
              <NumberInputField />
            </NumberInput>
          </FormControl>
        </SimpleGrid>

        <FormControl isRequired>
          <FormLabel>Items Sold</FormLabel>
          <Textarea
            value={itemsSold}
            onChange={(e) => setItemsSold(e.target.value.slice(0, 1000))}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Any Special Arrangements / Requests?</FormLabel>
          <Textarea
            value={specialArrangements}
            onChange={(e) => setSpecialArrangements(e.target.value.slice(0, 1000))}
          />
        </FormControl>

        <Heading size="sm" mt={4}>
          Admin Paid?
        </Heading>
        <SimpleGrid columns={[1, 2]} spacing={3}>
          <FormControl>
            <Select value={adminPaid} onChange={(e) => setAdminPaid(e.target.value)}>
              <option value="Yes">Yes</option>
              <option value="TBP">TBP</option>
              <option value="N/A">N/A</option>
            </Select>
          </FormControl>
          {adminPaid === 'Yes' && (
            <>
              <FormControl isRequired>
                <FormLabel>Paid Date</FormLabel>
                <Input
                  type="date"
                  value={adminPaidDate}
                  onChange={(e) => setAdminPaidDate(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Admin Payment Method</FormLabel>
                <Select
                  value={adminPaymentMethod}
                  onChange={(e) => setAdminPaymentMethod(e.target.value)}
                >
                  <option value="BT">Bank Transfer</option>
                  <option value="Card">Card</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="N/A">N/A</option>
                </Select>
              </FormControl>
            </>
          )}
          {adminPaid === 'TBP' && (
            <>
              <FormControl isRequired>
                <FormLabel>Target Date</FormLabel>
                <Input
                  type="date"
                  value={adminTbpDate}
                  onChange={(e) => setAdminTbpDate(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Planned Admin Payment Method</FormLabel>
                <Select
                  value={adminPaymentMethod}
                  onChange={(e) => setAdminPaymentMethod(e.target.value)}
                >
                  <option value="BT">Bank Transfer</option>
                  <option value="Card">Card</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="N/A">N/A</option>
                </Select>
              </FormControl>
            </>
          )}
        </SimpleGrid>

        <Heading size="sm" mt={4}>
          Contract Payment
        </Heading>
        <FormControl>
          <Select
            value={paymentType}
            onChange={(e) => {
              const v = e.target.value;
              setPaymentType(v);
              if (v === 'Cash' && !cashTerm) setCashTerm('preset1'); // default: 25% / 75%
            }}
          >
            <option value="">Select</option>
            <option value="Cash">Cash</option>
            <option value="Finance">Finance</option>
            <option value="Cash & Finance">Cash & Finance</option>
          </Select>
        </FormControl>

        {paymentType === 'Cash' && (
          <VStack align="stretch" spacing={3}>
            <FormControl isRequired>
              <FormLabel>Choose Term</FormLabel>
              <Select value={cashTerm} onChange={(e) => setCashTerm(e.target.value)}>
                <option value="preset1">25% Deposit – 75% On Completion</option>
                <option value="preset2">25% Deposit – 25% On Delivery – 50% On Completion</option>
                <option value="other">Other (details in Special Arrangements)</option>
              </Select>
            </FormControl>
            <SimpleGrid columns={[1, 2]} spacing={3}>
              <FormControl>
                <FormLabel>Deposit amount paid (£)</FormLabel>
                <NumberInput
                  min={0}
                  value={cashDepositAmount}
                  onChange={(v) => setCashDepositAmount(v)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>Deposit % (auto)</FormLabel>
                <Input value={cashDepositPct.toFixed(2)} readOnly />
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>Deposit payment method</FormLabel>
              <Select
                value={cashDepositMethod}
                onChange={(e) => setCashDepositMethod(e.target.value)}
              >
                <option value="BT">Bank Transfer</option>
                <option value="Card">Card</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
                <option value="N/A">N/A</option>
              </Select>
            </FormControl>
          </VStack>
        )}

        {(paymentType === 'Finance' || paymentType === 'Cash & Finance') && (
          <VStack align="stretch" spacing={3}>
            {paymentType === 'Cash & Finance' && (
              <>
                <SimpleGrid columns={[1, 2]} spacing={3}>
                  <FormControl isRequired>
                    <FormLabel>Cash amount (£)</FormLabel>
                    <NumberInput min={0} value={cfCashAmount} onChange={(v) => setCfCashAmount(v)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Cash % (auto)</FormLabel>
                    <Input value={cfCashPct.toFixed(2)} readOnly />
                  </FormControl>
                </SimpleGrid>
                <FormControl isRequired>
                  <FormLabel>Cash payment method</FormLabel>
                  <Select value={cfCashMethod} onChange={(e) => setCfCashMethod(e.target.value)}>
                    <option value="BT">Bank Transfer</option>
                    <option value="Card">Card</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Cash">Cash</option>
                    <option value="N/A">N/A</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Finance amount (£)</FormLabel>
                  <NumberInput
                    min={0}
                    value={cfFinanceAmount}
                    onChange={(v) => setCfFinanceAmount(v)}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </>
            )}

            <FormControl isRequired>
              <FormLabel>Finance Application Signed?</FormLabel>
              <Select
                value={financeSigned}
                onChange={(e) => setFinanceSigned(e.target.value)}
                placeholder="Select"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
            </FormControl>

            {financeSigned === 'Yes' && (
              <>
                <FormControl isRequired>
                  <FormLabel>Result</FormLabel>
                  <Select
                    value={financeResult}
                    onChange={(e) => setFinanceResult(e.target.value)}
                    placeholder="Select result"
                  >
                    <option value="Accepted">Accepted</option>
                    <option value="Pending">Pending</option>
                    <option value="Referred">Referred</option>
                    <option value="Declined">Declined</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Finance House</FormLabel>
                  <Select
                    value={financeHouse}
                    onChange={(e) => setFinanceHouse(e.target.value)}
                    placeholder="Select provider"
                  >
                    <option value="Omni">OMNI</option>
                    <option value="Novuna">Novuna</option>
                    <option value="Propensio">Propensio</option>
                    <option value="Shawbrook">Shawbrook</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormControl>
              </>
            )}
          </VStack>
        )}

        <Heading size="sm" mt={4}>
          Attachments (PDF only, up to 10 MB)
        </Heading>
        <SimpleGrid columns={[1, 1]} spacing={3}>
          <FormControl isRequired={settings.requireContractPdf}>
            <FormLabel>Contract PDF</FormLabel>
            <Input
              type="file"
              accept="application/pdf"
              onChange={(e) => setContractPdfFile(e.target.files?.[0] || null)}
            />
            {contractPdfUrl ? (
              <Text mt={1} fontSize="sm">
                <a href={contractPdfUrl} target="_blank" rel="noreferrer">
                  Download current contract.pdf
                </a>
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.600">
                {settings.requireContractPdf ? 'Required' : 'Optional'}
              </Text>
            )}
          </FormControl>
          <FormControl>
            <FormLabel>Doors PDF</FormLabel>
            <Input
              type="file"
              accept="application/pdf"
              onChange={(e) => setDoorsPdfFile(e.target.files?.[0] || null)}
            />
            {doorsPdfUrl ? (
              <Text mt={1} fontSize="sm">
                <a href={doorsPdfUrl} target="_blank" rel="noreferrer">
                  Download current doors.pdf
                </a>
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.600">
                Optional
              </Text>
            )}
          </FormControl>
        </SimpleGrid>

        <HStack spacing={3} mt={4}>
          <Button onClick={() => onClose && onClose()} variant="outline">
            Close
          </Button>
          <Button onClick={() => handleSave(true)} isLoading={saving} loadingText="Saving">
            Save changes
          </Button>
          {status !== 'unlocked' && (
            <Button onClick={unlock} variant="outline" colorScheme="blue">
              Unlock for rep
            </Button>
          )}
          {status !== 'submitted' && (
            <Button onClick={relockSubmit} colorScheme="purple" isLoading={saving}>
              Relock (submit)
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  );
}